import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DownloadTemplateSelection } from '@/entities/download-template';
import { API_TAGS, createLazyQuery } from '@/shared/api';
import { downloadFileFromBlob } from '@/shared/lib/utils/file';
import type { ExpertReportFeedbackFilters, ExpertReportListFilters } from './types';
import {
  createExpertReport,
  deleteExpertReport,
  downloadExpertReport,
  fetchAllExpertReports,
  fetchExpertReportBlockRatingHistory,
  fetchExpertReportBlockRule,
  fetchExpertReportBlocks,
  fetchExpertReportById,
  fetchExpertReportEvaluation,
  fetchExpertReportFeedback,
  fetchExpertReportRuleCommentHistory,
  fetchExpertReports,
  fetchExpertReportVerificationStatusHistory,
  fetchExpertReportVersions,
  fetchExpertReportVersionState,
  fetchPipelineScope,
  reorderExpertReportBlocks,
  updateExpertReport,
  updateExpertReportBlockManualRating,
  updateExpertReportRuleManualComment,
  updateExpertReportVerificationStatus,
} from './api';

export const expertReportKeys = {
  all: [API_TAGS.EXPERT_REPORTS] as const,
  lists: () => [...expertReportKeys.all, 'list'] as const,
  list: (filters: ExpertReportListFilters = {}) => [...expertReportKeys.lists(), { ...filters }] as const,
  fullList: (filters: Omit<ExpertReportListFilters, 'limit' | 'offset'> = {}) =>
    [...expertReportKeys.lists(), 'full', { ...filters }] as const,
  details: () => [...expertReportKeys.all, 'detail'] as const,
  detail: (id?: number | string) => [...expertReportKeys.details(), id] as const,
  versions: (id?: number) => [...expertReportKeys.detail(id), 'versions'] as const,
  version: (versionId?: number) => [...expertReportKeys.all, 'version', versionId] as const,
  evaluations: () => [...expertReportKeys.all, 'evaluation'] as const,
  evaluation: (conversationId: string | number, expertReportId: string | number) =>
    [...expertReportKeys.evaluations(), conversationId, expertReportId] as const,
  verificationStatusHistory: (conversationId: number, expertReportId: number) =>
    [...expertReportKeys.evaluation(conversationId, expertReportId), 'verification-status-history'] as const,
  ruleCommentHistory: (conversationId: number, expertReportId: number, ruleId: number) =>
    [...expertReportKeys.evaluation(conversationId, expertReportId), 'rule-comment-history', ruleId] as const,
  blockRatingHistory: (conversationId: number, expertReportId: number, blockId: number) =>
    [...expertReportKeys.evaluation(conversationId, expertReportId), 'block-rating-history', blockId] as const,
  rules: () => [...expertReportKeys.all, 'rule'] as const,
  rule: (ruleId: number) => [...expertReportKeys.rules(), ruleId] as const,
  blocks: (id: number) => [...expertReportKeys.detail(id), 'blocks'] as const,
  pipelineScope: () => [...expertReportKeys.all, 'pipeline-scope'] as const,
  feedback: (filters: ExpertReportFeedbackFilters = {}) =>
    [...expertReportKeys.all, 'feedback', { ...filters }] as const,
};

export const useExpertReports = (filters: ExpertReportListFilters = {}) => {
  return useQuery({
    queryKey: expertReportKeys.list(filters),
    queryFn: () => fetchExpertReports(filters),
  });
};

/**
 * Хук всего набора заключений под отбором: набор доходит до последнего заключения,
 * сколько бы окон выдачи он ни занимал. Нужен экранам, которые показывают заключения
 * перечнем и ищут по нему, — им первое окно выдачи за весь набор выдавать нельзя.
 * Набор занимает несколько запросов, поэтому у скрытого потребителя (закрытое окно)
 * его стоит откладывать через `enabled`.
 */
export const useAllExpertReports = (
  filters: Omit<ExpertReportListFilters, 'limit' | 'offset'> = {},
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: expertReportKeys.fullList(filters),
    queryFn: () => fetchAllExpertReports(filters),
    enabled: options?.enabled !== false,
  });
};

/** Как часто перезапрашивается заключение, пока идут пересчёт результатов по его диалогам */
const RESULTS_RECALCULATION_POLL_MS = 5_000;

export const useExpertReport = (id?: number | string) => {
  return useQuery({
    queryKey: expertReportKeys.detail(id),
    queryFn: () => fetchExpertReportById(id!),
    enabled: !!id?.toString(),
    // Пересчёт результатов по диалогам идёт на сервере сам по себе и ничего не присылает —
    // пока он не закончился, карточка перезапрашивается, чтобы признак пересчёта снялся сам
    refetchInterval: (query) => (query.state.data?.resultsRecalculating ? RESULTS_RECALCULATION_POLL_MS : false),
  });
};

export const useExpertReportVersions = (id?: number) => {
  return useQuery({
    queryKey: expertReportKeys.versions(id),
    queryFn: () => fetchExpertReportVersions(id!),
    enabled: typeof id === 'number' && id > 0,
  });
};

export const useExpertReportVersionState = (versionId?: number) => {
  return useQuery({
    queryKey: expertReportKeys.version(versionId),
    queryFn: () => fetchExpertReportVersionState(versionId!),
    enabled: typeof versionId === 'number' && versionId > 0,
  });
};

export const useCreateExpertReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createExpertReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expertReportKeys.lists() });
    },
  });
};

export const useUpdateExpertReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExpertReport,
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: expertReportKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: expertReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expertReportKeys.versions(id) });
    },
  });
};

/**
 * Мутация сохранения нового порядка и вложенности блоков заключения (drag-and-drop в дереве).
 *
 * Шлёт лёгкий метод перестановки `PUT /expert-report/{id}/blocks-order` — только позиции и
 * родители существующих блоков, без пересылки и повторной обработки правил, названия и
 * предиката. Сохранение быстрое, поэтому окно, в котором соседние блоки «скакали» и возникали
 * фантомные копии на медленном CPU, схлопывается.
 *
 * В отличие от {@link useUpdateExpertReport} НЕ инвалидирует `detail(id)`: новый порядок уже
 * применён оптимистично и подтверждается ответом сервера через `setQueryData`. Фоновый рефетч
 * `detail` во время drop-анимации dnd-kit ремоунтит дерево и оставляет «фантомную» копию
 * перетащенного блока внизу списка. История версий и список инвалидируются — их перерисовка
 * дерево не задевает.
 */
export const useReorderExpertReportBlocks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderExpertReportBlocks,
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(expertReportKeys.detail(id), data);
      queryClient.invalidateQueries({ queryKey: expertReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expertReportKeys.versions(id) });
    },
  });
};

export const useDeleteExpertReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteExpertReport,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: expertReportKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: expertReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expertReportKeys.versions(id) });
    },
  });
};

export const useExpertReportEvaluation = (conversationId?: number | string, expertReportId?: number | string) => {
  return useQuery({
    queryKey: expertReportKeys.evaluation(conversationId!, expertReportId!),
    queryFn: () => fetchExpertReportEvaluation(+conversationId!, +expertReportId!),
    enabled: !!conversationId && !!expertReportId,
  });
};

/**
 * Хук-мутация сохранения и сброса результата блока экспертного заключения, выставленного
 * пользователем: балла — у блока с баллами, значения справочника — у блока со справочником,
 * любой из двух форм — у блока с совмещённой шкалой.
 * Ответ сервера — весь результат оценки с пересчитанными итогами, им и заменяется кэш; истории, в
 * которые попадает правка, перезапрашиваются: ответ их не несёт.
 */
export const useUpdateExpertReportBlockManualRating = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExpertReportBlockManualRating,
    onSuccess: (data, { conversationId, expertReportId, blockId }) => {
      queryClient.setQueryData(expertReportKeys.evaluation(conversationId, expertReportId), data);
      // Правка переводит результат в проверку: пришедший статус виден в ответе, а вызванную им
      // смену показывает история — её ответ не несёт
      queryClient.invalidateQueries({
        queryKey: expertReportKeys.verificationStatusHistory(conversationId, expertReportId),
      });
      // Правка баллом, значением и возврат к результату системы становятся версиями в истории
      // результата блока
      queryClient.invalidateQueries({
        queryKey: expertReportKeys.blockRatingHistory(conversationId, expertReportId, blockId),
      });
    },
  });
};

/**
 * Хук-мутация сохранения и сброса изменённого комментария правила экспертного заключения и замечания к нему.
 * Ответ сервера — весь результат оценки, им и заменяется кэш; истории, в которые попадает правка,
 * перезапрашиваются: ответ их не несёт.
 */
export const useUpdateExpertReportRuleManualComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExpertReportRuleManualComment,
    onSuccess: (data, { conversationId, expertReportId, ruleId }) => {
      queryClient.setQueryData(expertReportKeys.evaluation(conversationId, expertReportId), data);
      // Правка переводит результат в проверку — смену показывает история статуса проверки
      queryClient.invalidateQueries({
        queryKey: expertReportKeys.verificationStatusHistory(conversationId, expertReportId),
      });
      // Правка, замечание и возврат к комментарию системы становятся версиями в истории комментария правила
      queryClient.invalidateQueries({
        queryKey: expertReportKeys.ruleCommentHistory(conversationId, expertReportId, ruleId),
      });
    },
  });
};

/**
 * Хук-мутация смены статуса проверки результата заключения человеком.
 * Ответ сервера — весь результат оценки, им и заменяется кэш; история смен перезапрашивается:
 * ответ её не несёт, а смена в неё попадает.
 */
export const useUpdateExpertReportVerificationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExpertReportVerificationStatus,
    onSuccess: (data, { conversationId, expertReportId }) => {
      queryClient.setQueryData(expertReportKeys.evaluation(conversationId, expertReportId), data);
      queryClient.invalidateQueries({
        queryKey: expertReportKeys.verificationStatusHistory(conversationId, expertReportId),
      });
    },
  });
};

/**
 * История смен статуса проверки результата заключения.
 *
 * Запрашивается только когда история показывается (`enabled`): у каждого заключения диалога она
 * своя, а открывают её заметно реже, чем сам результат.
 */
export const useExpertReportVerificationStatusHistory = (
  { conversationId, expertReportId }: { conversationId: number; expertReportId: number },
  enabled: boolean,
) => {
  return useQuery({
    queryKey: expertReportKeys.verificationStatusHistory(conversationId, expertReportId),
    queryFn: () => fetchExpertReportVerificationStatusHistory(conversationId, expertReportId),
    enabled,
  });
};

/**
 * История версий комментария правила в результате заключения.
 *
 * Запрашивается только когда история показывается (`enabled`): у каждого правила она своя, а
 * открывают её заметно реже, чем сам результат.
 */
export const useExpertReportRuleCommentHistory = (
  { conversationId, expertReportId, ruleId }: { conversationId: number; expertReportId: number; ruleId: number },
  enabled: boolean,
) => {
  return useQuery({
    queryKey: expertReportKeys.ruleCommentHistory(conversationId, expertReportId, ruleId),
    queryFn: () => fetchExpertReportRuleCommentHistory(conversationId, expertReportId, ruleId),
    enabled,
  });
};

/**
 * История версий результата блока в результате заключения.
 *
 * Запрашивается только когда история показывается (`enabled`): у каждого блока она своя, а
 * открывают её заметно реже, чем сам результат.
 */
export const useExpertReportBlockRatingHistory = (
  { conversationId, expertReportId, blockId }: { conversationId: number; expertReportId: number; blockId: number },
  enabled: boolean,
) => {
  return useQuery({
    queryKey: expertReportKeys.blockRatingHistory(conversationId, expertReportId, blockId),
    queryFn: () => fetchExpertReportBlockRatingHistory(conversationId, expertReportId, blockId),
    enabled,
  });
};

/**
 * Скачивание результатов заключения по выбранному шаблону: расширение имени файла берётся из
 * выбранного формата — заголовок ответа кросс-доменному запросу браузером не открывается.
 */
export const useDownloadExpertReport = () => {
  return useMutation({
    mutationFn: ({
      conversationId,
      expertReportId,
      templateId,
      format,
    }: {
      conversationId: number;
      expertReportId: number;
    } & DownloadTemplateSelection) => downloadExpertReport(conversationId, expertReportId, templateId, format),
    onSuccess: (blob, { conversationId, expertReportId, format }) => {
      downloadFileFromBlob(blob, `expert_report_${conversationId}_${expertReportId}.${format}`);
    },
  });
};

export const useExpertReportBlockRule = (ruleId?: number) => {
  return useQuery({
    // Ключ строится только по реальному идентификатору: без него ключ свёл бы в кэше
    // все вызовы хука, ждущие своего правила
    queryKey: ruleId ? expertReportKeys.rule(ruleId) : expertReportKeys.rules(),
    queryFn: () => fetchExpertReportBlockRule(ruleId!),
    enabled: !!ruleId,
    staleTime: Number.POSITIVE_INFINITY,
  });
};

/**
 * Хук окна списка правок, внесённых пользователями в результаты экспертных заключений.
 * Порция запрашивается списком по мере прокрутки; отбор и порядок задаются фильтрами.
 */
export const useLazyExpertReportFeedback = createLazyQuery(
  (filters: ExpertReportFeedbackFilters) => expertReportKeys.feedback(filters),
  fetchExpertReportFeedback,
);

/**
 * Хук получения дерева блоков экспертного заключения через GET /expert-report/{id}/blocks.
 * Используется для построения секции «Экспертные заключения» в настройке колонок таблицы диалогов.
 */
export const useExpertReportBlocks = (id?: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: expertReportKeys.blocks(id!),
    queryFn: () => fetchExpertReportBlocks(id!),
    enabled: typeof id === 'number' && id > 0 && options?.enabled !== false,
    staleTime: 60_000,
  });
};

/**
 * Хук перечня областей запуска генерации заключений через GET /expert-report/pipeline-scope:
 * заключения и деревья их узлов приходят одним ответом, поэтому выбор области любой глубины
 * обходится единственным запросом.
 */
export const usePipelineScope = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: expertReportKeys.pipelineScope(),
    queryFn: fetchPipelineScope,
    enabled: options?.enabled !== false,
    staleTime: 60_000,
  });
};
